import { Component, OnInit, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserSessionStore } from '../../shared/services/user-session.store';
import { LoginHelperService } from './login-helper.service';
import { environment } from '../../../environments/environment';
import { NgxTurnstileComponent } from 'ngx-turnstile';
import { AuthenticationService, Discovery } from 'src/app/shared/services/api/authentication/authentication.service';
import { ApiExStatisticsService } from 'src/app/shared/services/api/ex-statistics/api-ex-statistics.service';

@Component({
  templateUrl: 'login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit {
  protected readonly environment = environment;

  turnstileComponent = viewChild('turnstileComponent', { read: NgxTurnstileComponent });

  readonly cguUrl = 'https://s3-ao-writer.s3.eu-west-3.amazonaws.com/cgsu_explain_20260310.pdf';

  discoveryForm!: FormGroup;
  loginForm!: FormGroup;
  loading = true;
  isError = false;
  errorMessage = '';
  submitted = false;
  returnUrl = '/';
  returnQueryParams: Params = {};
  showPassword = false;
  captchaResetTimer?: NodeJS.Timeout;
  discoveryResult?: Discovery;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private sessionStore: UserSessionStore,
    private loginHelper: LoginHelperService,
    private authService: AuthenticationService,
    private router: Router,
    private apiExStatisticsService: ApiExStatisticsService
  ) {}

  async ngOnInit() {
    const fromUrl = this.route.snapshot.queryParams['fromUrl'];
    const loginHint = this.route.snapshot.queryParams['login_hint'];
    if (loginHint !== undefined) {
      this.router.navigate([], { queryParams: { login_hint: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }
    if (this.sessionStore.userSession()) {
      const parsed = this.loginHelper.parseReturnUrl(this.route.snapshot.queryParams);
      await this.loginHelper.navigateAfterLogin(fromUrl ? parsed.returnUrl : undefined, fromUrl ? parsed.returnQueryParams : undefined);
    } else {
      this.authService
        .authenticateWithCookie()
        .catch(() => null)
        .then(async result => {
          if (result) {
            const parsed = this.loginHelper.parseReturnUrl(this.route.snapshot.queryParams);
            const returnUrl = parsed.returnUrl;
            const returnQueryParams = parsed.returnQueryParams;
            this.loginHelper.navigateAfterLogin(returnUrl, returnQueryParams);
            this.apiExStatisticsService.setExplainStatistics();
            this.apiExStatisticsService.setAccountInfos();
          } else if (loginHint) {
            // Keep the loader displayed so step 1 is not shown before step 2
            await this.discoverFromLoginHint(loginHint);
          }
          this.loading = false;
        });
    }
    this.discoveryForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]],
    });
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      captcha: ['', Validators.required],
    });
    this.route.queryParams.subscribe(params => {
      const parsed = this.loginHelper.parseReturnUrl(params);
      this.returnUrl = parsed.returnUrl;
      this.returnQueryParams = parsed.returnQueryParams;
      if (params['error'] === 'force_sso' && params['sso_slug']) {
        this.authService.discover({ slug: params['sso_slug'] }).then(result => {
          if (result?.sso && result.authorization_url) {
            this.discoveryResult = result;
            window.location.href = result.authorization_url;
          }
        });
      }
      if (params['error'] === 'no_account') {
        this.isError = true;
        this.errorMessage = 'login.no-account';
      }
      if (params['error'] === 'sso_error') {
        this.isError = true;
        this.errorMessage = 'login.sso-error';
      }
      if (params['error'] === 'social_error') {
        this.isError = true;
        this.errorMessage = 'login.social-error';
      }
    });
  }

  async newLogin() {
    this.isError = false;
    this.errorMessage = '';
    this.showPassword = false;

    const loadingTimer = setTimeout(() => {
      this.loading = true;
    }, 300);

    try {
      await this.loginHelper.login(this.f['username'].value.trim(), this.f['password'].value, this.f['captcha'].value);
      await this.loginHelper.navigateAfterLogin(this.returnUrl, this.returnQueryParams);
    } catch (error) {
      const err = error as { status?: number };
      this.isError = true;
      switch (err.status) {
        case 429:
          this.errorMessage = 'login.error-too-many-requests';
          break;
        case 404:
          this.errorMessage = 'login.no-match';
          break;
        default:
          this.errorMessage = 'login.generic-error';
      }
      this.loginForm.controls['captcha'].setValue('');
      this.turnstileComponent()?.reset();
    } finally {
      clearTimeout(loadingTimer);
      this.loading = false;
    }
  }

  toggleFieldTextType() {
    this.showPassword = !this.showPassword;
  }

  get df() {
    return this.discoveryForm.controls;
  }

  get f() {
    return this.loginForm.controls;
  }

  handleCaptcha(token: string | null) {
    if (token) {
      this.loginForm.controls['captcha'].setValue(token);
      this.runCaptchaResetTimer();
    }
  }

  handleError() {
    this.isError = true;
    this.errorMessage = 'login.load-error';
    setTimeout(() => {
      this.isError = false;
      this.errorMessage = '';
      this.turnstileComponent()?.reset();
    }, 5000);
  }

  runCaptchaResetTimer() {
    clearTimeout(this.captchaResetTimer);
    this.captchaResetTimer = setTimeout(() => this.turnstileComponent()?.reset(), 60000 * 4.5);
  }

  async discover() {
    const email = this.df['email'].value.trim();
    this.f['username'].setValue(email);
    const fromUrl = this.route.snapshot.queryParams['fromUrl'];
    try {
      const result = await this.authService.discover({ email, redirect_url: fromUrl });
      if (result?.sso && result.authorization_url) {
        window.location.href = result.authorization_url;
      }
      this.discoveryResult = result;
      this.isError = false;
    } catch (error) {
      this.isError = true;
      this.errorMessage = 'login.generic-error';
    }
  }

  private async discoverFromLoginHint(loginHint: string) {
    this.df['email'].setValue(loginHint);
    if (this.df['email'].valid) {
      await this.discover();
    }
  }

  async socialSignin(provider: 'google' | 'microsoft') {
    this.isError = false;
    try {
      const result = await this.authService.socialSignin(provider, this.route.snapshot.queryParams['fromUrl']);
      window.location.href = result.authorization_url;
    } catch {
      this.isError = true;
      this.errorMessage = 'login.social-error';
    }
  }

  loginWithSSO() {
    if (this.discoveryResult?.sso && this.discoveryResult.slug) {
      const fromUrl = this.route.snapshot.queryParams['fromUrl'];
      this.router.navigate(['/sso', this.discoveryResult.slug], { queryParams: { fromUrl } });
    }
  }
}
